import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  collection,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  doc,
  setDoc,
  getDoc,
} from 'firebase/firestore';
import { auth, db, getIdToken } from '../firebase'; // make sure `db` (Firestore instance) is exported from firebase.js
import './Chats.css';

/* ─────────────────────────────────────────
   Helpers
───────────────────────────────────────── */

/** Deterministic conversation ID – always the same for two users */
function getConversationId(uid1, uid2) {
  return [uid1, uid2].sort().join('_');
}

function formatTime(timestamp) {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  if (isToday) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

/* ─────────────────────────────────────────
   Component
───────────────────────────────────────── */

export default function Chats() {
  const [searchParams] = useSearchParams();
  const preselectedUid = searchParams.get('user');

  const currentUser = auth.currentUser;

  // Sidebar: list of connections
  const [connections, setConnections] = useState([]);
  const [loadingConnections, setLoadingConnections] = useState(true);

  // Active conversation
  const [activeConn, setActiveConn] = useState(null); // { uid, username, email, profilePicUrl }
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);

  const messagesEndRef = useRef(null);
  const unsubscribeRef = useRef(null);
  const inputRef = useRef(null);

  /* ── Fetch connections once ── */
  useEffect(() => {
    if (!currentUser) return;
    (async () => {
      try {
        const token = await getIdToken();
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/user/${currentUser.uid}/connections`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setConnections(res.data);
      } catch (err) {
        console.error('Error fetching connections:', err);
      } finally {
        setLoadingConnections(false);
      }
    })();
  }, [currentUser]);

  /* ── Auto-select user from query param ── */
  useEffect(() => {
    if (preselectedUid && connections.length > 0) {
      const found = connections.find(c => c.uid === preselectedUid);
      if (found) openConversation(found);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectedUid, connections]);

  /* ── Scroll to bottom on new messages ── */
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  /* ── Open / switch conversation ── */
  const openConversation = useCallback(
    (conn) => {
      if (activeConn?.uid === conn.uid) return;

      // Unsubscribe from previous listener
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }

      setActiveConn(conn);
      setMessages([]);
      setLoadingMessages(true);

      const convId = getConversationId(currentUser.uid, conn.uid);
      const msgsRef = collection(db, 'conversations', convId, 'messages');
      const q = query(msgsRef, orderBy('createdAt', 'asc'));

      unsubscribeRef.current = onSnapshot(q, (snapshot) => {
        const msgs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        setMessages(msgs);
        setLoadingMessages(false);
      });

      inputRef.current?.focus();
    },
    [activeConn, currentUser]
  );

  /* ── Cleanup listener on unmount ── */
  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) unsubscribeRef.current();
    };
  }, []);

  /* ── Send message ── */
  const handleSend = async () => {
    const text = input.trim();
    if (!text || !activeConn || sending) return;

    setSending(true);
    setInput('');

    try {
      const convId = getConversationId(currentUser.uid, activeConn.uid);
      const convRef = doc(db, 'conversations', convId);

      // Ensure conversation document exists (for metadata / future features)
      const convSnap = await getDoc(convRef);
      if (!convSnap.exists()) {
        await setDoc(convRef, {
          participants: [currentUser.uid, activeConn.uid],
          createdAt: serverTimestamp(),
        });
      }

      // Add message
      await addDoc(collection(db, 'conversations', convId, 'messages'), {
        text,
        senderId: currentUser.uid,
        senderName: currentUser.displayName || 'You',
        createdAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('Failed to send message:', err);
      setInput(text); // restore on failure
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  /* ─────────────────────────────────────────
     Render
  ───────────────────────────────────────── */

  return (
    <div className="chats-root">
      {/* ── Sidebar ── */}
      <aside className="chats-sidebar">
        <div className="sidebar-header">
          <span className="sidebar-title">Messages</span>
          <span className="sidebar-count">{connections.length}</span>
        </div>

        {loadingConnections && (
          <div className="sidebar-loading">
            {[1, 2, 3].map(i => (
              <div key={i} className="skeleton-row">
                <div className="skeleton-avatar" />
                <div className="skeleton-text">
                  <div className="skeleton-line short" />
                  <div className="skeleton-line" />
                </div>
              </div>
            ))}
          </div>
        )}

        {!loadingConnections && connections.length === 0 && (
          <div className="sidebar-empty">
            <span className="empty-icon">👥</span>
            <p>No connections yet.<br />Connect with peers first.</p>
          </div>
        )}

        <ul className="connections-list">
          {connections.map(conn => (
            <li
              key={conn.uid}
              className={`connection-item ${activeConn?.uid === conn.uid ? 'active' : ''}`}
              onClick={() => openConversation(conn)}
            >
              <div className="conn-avatar-wrap">
                <img
                  src={conn.profilePicUrl || '/default-profile.png'}
                  alt={conn.username}
                  className="conn-avatar"
                />
                <span className="conn-status-dot" />
              </div>
              <div className="conn-meta">
                <span className="conn-name">{conn.username}</span>
                <span className="conn-email">{conn.email}</span>
              </div>
            </li>
          ))}
        </ul>
      </aside>

      {/* ── Conversation pane ── */}
      <main className="chats-main">
        {!activeConn ? (
          <div className="no-conversation">
            <div className="no-conv-icon">💬</div>
            <h3>Select a conversation</h3>
            <p>Pick a connection from the left to start chatting.</p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="chat-header">
              <img
                src={activeConn.profilePicUrl || '/default-profile.png'}
                alt={activeConn.username}
                className="chat-header-avatar"
              />
              <div className="chat-header-info">
                <span className="chat-header-name">{activeConn.username}</span>
                <span className="chat-header-email">{activeConn.email}</span>
              </div>
            </div>

            {/* Messages */}
            <div className="messages-area">
              {loadingMessages && (
                <div className="messages-loading">
                  <div className="spinner" />
                </div>
              )}

              {!loadingMessages && messages.length === 0 && (
                <div className="messages-empty">
                  <span>No messages yet — say hello! 👋</span>
                </div>
              )}

              {messages.map((msg, idx) => {
                const isMine = msg.senderId === currentUser.uid;
                const prevMsg = messages[idx - 1];
                const showDateDivider =
                  idx === 0 ||
                  (prevMsg?.createdAt &&
                    msg?.createdAt &&
                    new Date(msg.createdAt.toDate?.() || msg.createdAt).toDateString() !==
                      new Date(prevMsg.createdAt.toDate?.() || prevMsg.createdAt).toDateString());

                return (
                  <React.Fragment key={msg.id}>
                    {showDateDivider && msg.createdAt && (
                      <div className="date-divider">
                        <span>
                          {new Date(
                            msg.createdAt.toDate ? msg.createdAt.toDate() : msg.createdAt
                          ).toLocaleDateString([], {
                            weekday: 'long',
                            month: 'long',
                            day: 'numeric',
                          })}
                        </span>
                      </div>
                    )}
                    <div className={`message-row ${isMine ? 'mine' : 'theirs'}`}>
                      {!isMine && (
                        <img
                          src={activeConn.profilePicUrl || '/default-profile.png'}
                          alt={activeConn.username}
                          className="msg-avatar"
                        />
                      )}
                      <div className="bubble-wrap">
                        <div className={`bubble ${isMine ? 'bubble-mine' : 'bubble-theirs'}`}>
                          {msg.text}
                        </div>
                        <span className="msg-time">{formatTime(msg.createdAt)}</span>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="chat-input-bar">
              <textarea
                ref={inputRef}
                className="chat-textarea"
                placeholder={`Message ${activeConn.username}…`}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={1}
              />
              <button
                className={`send-btn ${sending ? 'sending' : ''}`}
                onClick={handleSend}
                disabled={!input.trim() || sending}
                aria-label="Send message"
              >
                {sending ? (
                  <span className="send-spinner" />
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="22" y1="2" x2="11" y2="13" />
                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                  </svg>
                )}
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
